/**
 * What an import changed or left out, for the review to show before anything is stored (part 18 log, principle 3,
 * table FL). Each flag is counted, and tied to the chapters it touches through the top-level block it was met in.
 */

/** Table FL, in the order the review lists them. */
export const FLAG_KINDS = Object.freeze([
  'table',
  'tracked',
  'comments',
  'hidden',
  'underline',
  'strike',
  'superscript',
  'subscript',
  'caps',
  'link',
  'contents',
  'embedded',
  'macros',
  'graphic',
  'equation',
  'symbol',
  'textbox',
  'endnotes',
  'missing-note',
  'deep-heading',
  'linked-image',
  'image-format',
  'image-large',
  'image-missing',
  'image-count',
  'not-split',
  'structure',
  'formatting-kept-out',
  'encoding',
  'code',
  'html',
  'indented',
  'outside-image',
  'pandoc',
] as const);

/** One kind of flag (table FL). */
export type FlagKind = (typeof FLAG_KINDS)[number];

/** A flag as the review shows it: how many times, and in which chapters (indexes, ascending; none for the whole book). */
export interface ImportFlag {
  readonly kind: FlagKind;
  readonly count: number;
  readonly chapters: readonly number[];
}

/** Counts flags as a converter meets them, each at a top-level block (or none, for the file as a whole). */
export class FlagRecorder {
  readonly #met = new Map<FlagKind, { count: number; readonly blocks: Set<number> }>();

  /** `count` more of `kind`, met in top-level block `block` (null: the file as a whole). */
  add(kind: FlagKind, block: number | null, count = 1): void {
    let met = this.#met.get(kind);
    if (met === undefined) {
      met = { count: 0, blocks: new Set() };
      this.#met.set(kind, met);
    }
    met.count += count;
    if (block !== null) met.blocks.add(block);
  }

  /** The flags in table order, each block mapped to its chapter by `chapterOf` (null: in none, part 23a's matter). */
  flags(chapterOf: (block: number) => number | null): ImportFlag[] {
    return FLAG_KINDS.flatMap((kind): ImportFlag[] => {
      const met = this.#met.get(kind);
      if (met === undefined) return [];
      const chapters = [...met.blocks].flatMap((block) => {
        const chapter = chapterOf(block);
        return chapter === null ? [] : [chapter];
      });
      return [{ kind, count: met.count, chapters: [...new Set(chapters)].sort((a, b) => a - b) }];
    });
  }
}
